// src/views/auth/Register.js
//
// The sign-in page's quieter twin: the same white panel and logo, no teal
// band, because the form itself is long enough to fill the card.
// Fields, validation and submission are unchanged.
import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';

import { register as registerAction, clearError } from '../../store/auth/authSlice';

const registerSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  email: z.string().min(1, 'Email is required').email('Invalid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(1, 'Confirm Password is required'),
  firstName: z.string().optional().or(z.literal('')),
  lastName: z.string().optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords must match',
  path: ['confirmPassword'],
});

const adornment = (Icon) => ({
  startAdornment: (
    <InputAdornment position="start">
      <Icon fontSize="small" />
    </InputAdornment>
  ),
});

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: '',
    },
  });

  const onSubmit = (values) => {
    const { username, email, password, firstName, lastName, phone } = values;

    dispatch(registerAction({ username, email, password, firstName, lastName, phone }))
      .unwrap()
      .then(() => {
        navigate('/login');
      });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 560,
          width: '100%',
          p: { xs: 3, sm: 5 },
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            component="img"
            src="/assets/logo.png"
            alt="AiViz"
            sx={{ height: 34, mb: 3.5, display: 'block' }}
          />

          <Typography variant="h2" component="h1">
            Create an account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
            An administrator assigns your role once the account exists.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            size="medium"
            label="Username"
            autoComplete="username"
            error={!!errors.username}
            helperText={errors.username?.message}
            sx={{ mb: 2 }}
            InputProps={adornment(PersonIcon)}
            {...register('username')}
          />

          <TextField
            fullWidth
            size="medium"
            label="Email"
            type="email"
            autoComplete="email"
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={{ mb: 2 }}
            InputProps={adornment(EmailIcon)}
            {...register('email')}
          />

          <TextField
            fullWidth
            size="medium"
            label="Password"
            type="password"
            autoComplete="new-password"
            error={!!errors.password}
            helperText={errors.password?.message}
            sx={{ mb: 2 }}
            InputProps={adornment(LockIcon)}
            {...register('password')}
          />

          <TextField
            fullWidth
            size="medium"
            label="Confirm Password"
            type="password"
            autoComplete="new-password"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            sx={{ mb: 2 }}
            InputProps={adornment(LockIcon)}
            {...register('confirmPassword')}
          />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="medium"
                label="First Name"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                InputProps={adornment(BadgeIcon)}
                {...register('firstName')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="medium"
                label="Last Name"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                InputProps={adornment(BadgeIcon)}
                {...register('lastName')}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            size="medium"
            label="Phone Number"
            error={!!errors.phone}
            helperText={errors.phone?.message}
            sx={{ mb: 3 }}
            InputProps={adornment(PhoneIcon)}
            {...register('phone')}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{ py: 1.1 }}
          >
            {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Create account'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography
              component={Link}
              to="/login"
              variant="body2"
              sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
            >
              Already have an account? Sign in
            </Typography>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default Register;
